import type { OptionIdentifier } from '@/types/optionDiscovery';
import { volatilityService } from '@services/volatilityService';
import { calculateDelta, getImpliedVolatility } from '@utils/option-math';

export interface CalculateLambdaParams {
  option: OptionIdentifier;
  spotPrice: number;
  optionPrice: number;
  volatility?: number;
}

/**
 * Calculate Lambda (leverage) for an option
 * Lambda = |Delta| × (Spot Price / Option Price)
 *
 * @param params - Parameters including option, spot price, option price, and optional volatility override
 * @returns Lambda value, or null if calculation cannot be performed
 */
export function calculateLambda({
  option,
  spotPrice,
  optionPrice,
  volatility: volatilityOverride,
}: CalculateLambdaParams): number | null {
  if (optionPrice <= 0 || spotPrice <= 0) {
    return null;
  }

  const baseVolatility = volatilityOverride ?? volatilityService.getVolatility(option.asset);
  if (!baseVolatility || baseVolatility <= 0) {
    return null;
  }

  // Apply moneyness-based volatility skew to match pricing model
  const adjustedVolatility = getImpliedVolatility(
    spotPrice,
    option.strikePrice,
    baseVolatility,
    10.0, // Same skew factor as pricing
  );

  const timeToExpiryMs = option.expiryTimestamp - Date.now();
  if (timeToExpiryMs <= 0) {
    return null;
  }

  // Convert time to expiry from milliseconds to years
  const timeToExpiryYears = timeToExpiryMs / (365 * 24 * 60 * 60 * 1000);

  // Calculate delta using skew-adjusted volatility
  const delta = calculateDelta({
    underlyingPrice: spotPrice,
    strikePrice: option.strikePrice,
    timeToExpiry: timeToExpiryYears,
    riskFreeRate: 0.04, // 4% risk-free rate
    volatility: adjustedVolatility,
    optionType: option.type === 'CALL' ? 'call' : 'put',
  });

  // Lambda = Delta × (Spot Price / Option Price)
  return Math.abs(delta) * (spotPrice / optionPrice);
}

